import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'

dayjs.extend(customParseFormat)
dayjs.extend(utc)

/**
 * Convert Excel serial date to JavaScript Date
 * Excel stores dates as days since December 30, 1899
 */
function excelSerialToDate (serial: number): Date {
  // Excel epoch: December 30, 1899 (serial numbering includes Excel's 1900 leap-year quirk)
  const excelEpoch = new Date(Date.UTC(1899, 11, 30))
  const msPerDay = 24 * 60 * 60 * 1000
  return new Date(excelEpoch.getTime() + serial * msPerDay)
}

/**
 * Check if a value looks like an Excel serial date
 * Valid Excel dates for reasonable years (1900-2100) are roughly 1-73050
 */
function isExcelSerialDate (value: number): boolean {
  return Number.isFinite(value) && value >= 1 && value <= 73050
}

/**
 * Normalizes a date string to YYYY-MM-DD format using dayjs
 * Handles various formats including:
 * - ISO dates (YYYY-MM-DD)
 * - Partial dates (YYYY-MM, MM/YYYY)
 * - Slash formats (DD/MM/YYYY, YYYY/MM/DD)
 * - Text months (April 2022, 15 April 2022, April 15, 2022)
 * - Excel serial dates (numbers like 44896)
 */
export default function normalizeDateToISO (date: string | number | undefined): string | undefined {
  if (date === undefined || date === null || date === '') {
    return undefined
  }

  // Handle Excel serial dates (numbers)
  if (typeof date === 'number' && isExcelSerialDate(date)) {
    const jsDate = excelSerialToDate(date)
    return dayjs.utc(jsDate).format('YYYY-MM-DD')
  }

  // Handle numeric strings that look like Excel serial dates
  const numericValue = typeof date === 'string' ? parseFloat(date) : NaN
  if (!isNaN(numericValue) && isExcelSerialDate(numericValue) && /^\d+(\.\d+)?$/.test(String(date).trim())) {
    const jsDate = excelSerialToDate(numericValue)
    return dayjs.utc(jsDate).format('YYYY-MM-DD')
  }

  const trimmed = String(date).trim()

  // Try common formats with dayjs (strict mode)
  const formats = [
    'YYYY-MM-DD',
    'YYYY-MM',
    'YYYY-M',
    'DD/MM/YYYY',
    'D/M/YYYY',
    'MM/YYYY',
    'M/YYYY',
    'YYYY/MM/DD',
    'YYYY/M/D',
    'MMMM YYYY',
    'MMM YYYY',
    'D MMMM YYYY',
    'DD MMMM YYYY',
    'MMMM D, YYYY',
    'MMMM DD, YYYY',
    'MMM D, YYYY',
    'MMM DD, YYYY'
  ]

  for (const format of formats) {
    const parsed = dayjs(trimmed, format, true)
    if (parsed.isValid()) {
      return parsed.format('YYYY-MM-DD')
    }
  }

  // Fallback: try dayjs without strict format (handles many natural formats)
  const fallback = dayjs(trimmed)
  if (fallback.isValid()) {
    return fallback.format('YYYY-MM-DD')
  }

  // Return undefined for unparseable dates - let validation catch invalid dates
  return undefined
}
